import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  MarketingDataVersionProbeDocument,
  MarketingDataVersionProbeQuery,
  MarketingDataVersionProbeQueryVariables,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { marketingTodayWarsaw } from './marketingModel';

/** Шаг опроса версии, пока ждём пересчёта. */
export const MARKETING_RECALC_POLL_MS = 10 * 1000;

/** Дольше не ждём: пересчёт идёт 1–2 минуты, пять — с большим запасом. */
export const MARKETING_RECALC_MAX_MS = 5 * 60 * 1000;

export interface IMarketingRecalcWatch {
  /** Идёт ожидание пересчёта. */
  watching: boolean;
  /** Начать ждать (правка справочника, «Обновить сейчас»). */
  watch: () => void;
}

/**
 * ТЗ 0088 — ожидание пересчёта сводок после правки.
 *
 * Правка справочника поднимает `dataVersion` сразу после коммита и ставит
 * пересборку; когда пересборка закончится, версия вырастет ещё раз. Поэтому
 * точка отсчёта — версия ПОСЛЕ ответа мутации, а ждём мы следующей.
 *
 * Опрос — лёгким запросом версии (окно в один день, сервер кеширует ответ до
 * новой версии), раз в 10 с, не дольше 5 минут. Дождались или вышло время —
 * `onDone(changed)`: страница перечитывает открытую вкладку в любом случае,
 * а тост «цифры пересчитаны» хук поднимает, только если версия действительно
 * сменилась.
 *
 * Таймер — `setTimeout`, не `requestAnimationFrame`: в фоновой вкладке rAF
 * не срабатывает (грабли `raf-does-not-fire-in-hidden-tab`). Новый `watch`
 * отменяет прежнее ожидание; размонтирование — тоже.
 */
export const useMarketingRecalcWatch = (
  onDone: (changed: boolean) => void,
  enabled = true,
): IMarketingRecalcWatch => {
  const client = useApolloClient();
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [watching, setWatching] = useState(false);
  const generation = useRef(0);
  const timer = useRef<number | null>(null);
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(
    () => () => {
      generation.current += 1;
      clearTimer();
    },
    [],
  );

  const probe = useCallback(async (): Promise<number | null> => {
    const today = marketingTodayWarsaw();
    try {
      const { data } = await client.query<
        MarketingDataVersionProbeQuery,
        MarketingDataVersionProbeQueryVariables
      >({
        query: MarketingDataVersionProbeDocument,
        variables: { filter: { dateFrom: today, dateTo: today } },
        fetchPolicy: 'network-only',
      });
      return data?.marketingOverview?.dataVersion ?? null;
    } catch {
      return null;
    }
  }, [client]);

  const watch = useCallback(() => {
    if (!enabled) return;
    generation.current += 1;
    const id = generation.current;
    clearTimer();
    setWatching(true);
    const started = Date.now();

    const finish = (changed: boolean) => {
      if (generation.current !== id) return;
      clearTimer();
      setWatching(false);
      if (changed) {
        addToast({
          data: {
            type: ToastTypes.info,
            title: t('marketing.toast.recalcDone'),
          },
          type: GlobalToastTypeNames.Marketing,
        });
      }
      onDoneRef.current(changed);
    };

    probe().then((baseline) => {
      if (generation.current !== id) return;
      const tick = async () => {
        if (generation.current !== id) return;
        const version = await probe();
        if (generation.current !== id) return;
        if (baseline !== null && version !== null && version !== baseline) {
          finish(true);
          return;
        }
        if (Date.now() - started >= MARKETING_RECALC_MAX_MS) {
          finish(false);
          return;
        }
        timer.current = window.setTimeout(tick, MARKETING_RECALC_POLL_MS);
      };
      timer.current = window.setTimeout(tick, MARKETING_RECALC_POLL_MS);
    });
  }, [addToast, enabled, probe, t]);

  return { watching, watch };
};
